import {TaskListQueryItem} from "../types/task-list-query.item.type";

/** The most entries the tasklist keeps rendered at once (measured: no long task at 500 cards). */
export const TASKLIST_BLOCK_MAX = 500;

/** Entries added per extension at one edge of the block. */
export const TASKLIST_EXTEND_STEP = 20;

/** Entries added per extension while the scrollbar is dragged (fewer re-grabs of the thumb). */
export const TASKLIST_EXTEND_STEP_DRAG = 200;

/**
 * The rendered part of the list, held by the keys of its first and last entry rather than by
 * indices, so it still fits after the list has been reloaded and indices have shifted.
 */
export type TasklistBlockBounds = {
    firstKey: string,
    lastKey: string,
};

export type TasklistBlockSide = 'top' | 'bottom';

/**
 * Pure logic of the tasklist's block model: which contiguous part of the list is rendered.
 *
 * The list holds every entry; only a block of real, measured cards around a null point is in the
 * DOM, and it grows at its edges as the user scrolls. Nothing here touches the DOM or estimates a
 * height — how much lead-in is enough is measured by the view on what it has rendered.
 */
export class TasklistWindowHelper {

    /**
     * The uid the block is built around, from the stored anchor and the list INCLUDING deleted
     * entries: a live anchor as is; a deleted anchor gives the next following live issue, or the
     * nearest live one before when none follows. Null ("today") when nothing is stored, the anchor
     * is not in the list, or no live entry exists.
     */
    public static resolveNullPoint(entries: readonly TaskListQueryItem[], anchorUid: string | null): string | null {
        if (anchorUid === null) {
            return null;
        }

        const anchorIndex = entries.findIndex(entry => entry.uid === anchorUid);
        if (anchorIndex < 0) {
            return null;
        }

        for (let index = anchorIndex; index < entries.length; index++) {
            if (entries[index].isDeleted !== true) {
                return entries[index].uid;
            }
        }

        for (let index = anchorIndex - 1; index >= 0; index--) {
            if (entries[index].isDeleted !== true) {
                return entries[index].uid;
            }
        }

        return null;
    }

    /**
     * The first block: `leadIn` entries above the null point, `size` entries in total, clamped to
     * the list. A null point that is not in the list builds from the list start. Null for an empty
     * list.
     */
    public static buildAround(keys: readonly string[], nullKey: string, leadIn: number, size: number): TasklistBlockBounds | null {
        if (keys.length === 0) {
            return null;
        }

        const nullIndex = Math.max(0, keys.indexOf(nullKey));
        let start = Math.max(0, nullIndex - leadIn);
        const end = Math.min(keys.length - 1, start + size - 1);
        start = Math.max(0, Math.min(start, end - size + 1));

        return {firstKey: keys[start], lastKey: keys[end]};
    }

    /**
     * Whether the block can still grow on `side` — false at the list start (top) or end (bottom).
     * This is the condition that ends the view's extension loop; without it the loop would run on at
     * the list end, where the bottom marker stays near.
     */
    public static canExtend(keys: readonly string[], bounds: TasklistBlockBounds, side: TasklistBlockSide): boolean {
        const range = TasklistWindowHelper.resolve(keys, bounds);
        if (range === null) {
            return false;
        }

        return side === 'top' ? range.start > 0 : range.end < keys.length - 1;
    }

    /**
     * The block grown by `step` entries on `side`, clamped to the list. Unchanged when the bounds no
     * longer resolve against `keys`.
     */
    public static extend(keys: readonly string[], bounds: TasklistBlockBounds, side: TasklistBlockSide, step: number): TasklistBlockBounds {
        const range = TasklistWindowHelper.resolve(keys, bounds);
        if (range === null) {
            return bounds;
        }

        if (side === 'top') {
            return {firstKey: keys[Math.max(0, range.start - step)], lastKey: bounds.lastKey};
        }

        return {firstKey: bounds.firstKey, lastKey: keys[Math.min(keys.length - 1, range.end + step)]};
    }

    /**
     * The block trimmed to at most `max` entries on the side away from `grownSide`.
     *
     * When trimming the top, `protectedKey` (the "today" divider) is never trimmed off while entries
     * after it are in the block: the cards after it are coloured by a sibling selector on that divider.
     * The block may then exceed `max` by the entries after "today" (measured: 14 and 9 on real data).
     */
    public static cap(
        keys: readonly string[],
        bounds: TasklistBlockBounds,
        grownSide: TasklistBlockSide,
        max: number,
        protectedKey: string | null
    ): TasklistBlockBounds {
        const range = TasklistWindowHelper.resolve(keys, bounds);
        if (range === null || range.end - range.start + 1 <= max) {
            return bounds;
        }

        if (grownSide === 'top') {
            return {firstKey: bounds.firstKey, lastKey: keys[range.start + max - 1]};
        }

        let start = range.end - max + 1;
        const protectedIndex = protectedKey === null ? -1 : keys.indexOf(protectedKey);
        if (protectedIndex >= range.start && protectedIndex < start && protectedIndex < range.end) {
            start = protectedIndex;
        }

        return {firstKey: keys[start], lastKey: bounds.lastKey};
    }

    /** The block's inclusive index range in `keys`, or null when a bound key is not in it. */
    public static resolve(keys: readonly string[], bounds: TasklistBlockBounds): { start: number, end: number } | null {
        const start = keys.indexOf(bounds.firstKey);
        const end = keys.indexOf(bounds.lastKey);
        if (start < 0 || end < 0 || end < start) {
            return null;
        }

        return {start, end};
    }

    /**
     * The block carried over a reload: each bound that vanished is replaced by the nearest entry of
     * the OLD block that survived, searching inwards. Null when no entry of the old block survived.
     */
    public static remapAfterReload(
        oldKeys: readonly string[],
        newKeys: readonly string[],
        bounds: TasklistBlockBounds
    ): TasklistBlockBounds | null {
        const range = TasklistWindowHelper.resolve(oldKeys, bounds);
        if (range === null) {
            return null;
        }

        const surviving = new Set(newKeys);
        let first: string | null = null;
        for (let index = range.start; index <= range.end; index++) {
            if (surviving.has(oldKeys[index])) {
                first = oldKeys[index];
                break;
            }
        }

        let last: string | null = null;
        for (let index = range.end; index >= range.start; index--) {
            if (surviving.has(oldKeys[index])) {
                last = oldKeys[index];
                break;
            }
        }

        if (first === null || last === null) {
            return null;
        }

        return {firstKey: first, lastKey: last};
    }
}
